import { Mark15 } from './mark-15';

/**
 * PRIMARY HORIZONTAL LOCKUP — mark, wordmark, rule, descriptor.
 *
 * WHAT CHANGED AND WHY (2026-09-05). This used to be the sliced italic wordmark: SHIFT
 * cut through with skewed slats, no mark at all. That came from the earlier asset pack,
 * whose guidelines made the wordmark the identity and the sunset composition a secondary
 * device. The delivered brand board reverses it in as many words — "CONCEPT 15 / PRIMARY
 * LOGO ... The #15 horizon and commercial building are the logo. The wordmark and
 * operational devices extend its after-hours story." So the mark leads and the wordmark is
 * set plainly now: upright, unsliced, no skew. Nothing competes with the mark.
 *
 * EVERY PROPORTION IS MEASURED OFF THE BOARD, not chosen. Sampling the delivered
 * brand-board.png gives, with M = the mark's height:
 *     gap mark -> type   0.32M      wordmark cap height  0.484M
 *     rule thickness     0.028M     rule sits            0.17M under the cap line
 *     descriptor cap     0.102M     and starts           0.16M under the rule
 * They are expressed in CSS as multiples of `--m`, so the whole lockup scales from one
 * number and can never drift out of the board's ratios.
 *
 * `--m` IS FLUID, AND THAT IS LOAD-BEARING (fixed 2026-09-06).
 * The first version wrote `--m: 96px` as a flat inline style and tried to shrink it on
 * small screens with a `@media (max-width: 32rem)` rule in app.css. That rule could never
 * win: **an inline custom-property declaration beats any stylesheet rule**, media query or
 * not. So the footer lockup stayed 96px on a 390px phone, ran 447px wide, and pushed the
 * whole document 57px past the viewport — which makes a phone browser widen its layout
 * viewport and render the entire site zoomed out. One un-shrinkable logo did that.
 *
 * The fix is to make the inline value itself responsive rather than to fight it from
 * outside: `min(<height>px, <cap>vw)`. `height` is the size it wants; the vw term is the
 * ceiling that keeps it inside the screen. The whole lockup is about 5.2 x M wide
 * (mark 1.603M + gap 0.32M + wordmark ~3.25M), so at 16vw it occupies ~83% of the
 * viewport and clears the page gutters at every width. Never reintroduce a flat px here.
 *
 * THE ONE PLACE WE DEPART FROM THE BOARD, AND WHY. At the board's ratio the descriptor is
 * ~5px tall in a 34px header — unreadable, and rendering it anyway would be worse than
 * honouring it. So the descriptor appears only at and above the size where it is actually
 * legible (~9px cap, i.e. a mark >= 88px: footer, OG image, print). That is a legibility
 * floor, not a redesign: at poster scale the full three-part lockup on the board renders.
 */

/** Below this mark height the amber descriptor is smaller than 9px cap and is dropped. */
const DESCRIPTOR_MIN_MARK_HEIGHT = 88;

/** Share of the viewport the mark's height may claim. See the fluid note above. */
const DEFAULT_VW_CAP = 16;

export function Lockup({
  height = 34,
  showDescriptor,
  vwCap = DEFAULT_VW_CAP,
}: {
  height?: number;
  /** Override the automatic legibility floor. Omit it and the right thing happens. */
  showDescriptor?: boolean;
  /**
   * Ceiling on `--m` as a share of viewport width. Raise it only for a lockup that is
   * NOT competing for horizontal room with anything else on its line.
   */
  vwCap?: number;
}) {
  const withDescriptor = showDescriptor ?? height >= DESCRIPTOR_MIN_MARK_HEIGHT;

  return (
    <span
      className="lockup"
      style={{ ['--m' as string]: `min(${height}px, ${vwCap}vw)` }}
    >
      {/*
        `height` here is only the PRE-STYLESHEET fallback. `.lockup .mark15` in app.css sets
        `height: var(--m); width: auto`, and CSS always beats an SVG presentation attribute,
        so the mark still scales fluidly once styles apply.

        It has to be present, though. Dropping it entirely (the first attempt at this fix)
        left the SVG with no intrinsic size, so between DOMContentLoaded and the stylesheet
        landing the browser fell back to a default box and the page overflowed ~50px —
        measured, and it resolved to 0 by `load`. That is a real horizontal-scroll flash on
        a slow phone, not a test artifact. Keep the attribute; let CSS win afterwards.

        Detail switches on the AUTHORED size, which is the right signal — a header lockup
        is compact whatever the viewport later does to it.
      */}
      <Mark15 height={height} detail={height < 48 ? 'compact' : 'full'} />
      <span className="lockup-type">
        <span className="lockup-word">Second Shift</span>
        {withDescriptor && <span className="lockup-desc">Commercial Cleaning</span>}
      </span>
    </span>
  );
}
